import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Truck, Building2, Users, ArrowRight, MapPin, Star, DollarSign } from "lucide-react";

// State metadata for SEO and content
const STATE_META = {
  illinois:       { name: "Illinois",       abbr: "IL", city: "Chicago",      region: "Midwest",     desc: "hub for OTR and regional CDL-A drivers" },
  texas:          { name: "Texas",           abbr: "TX", city: "Dallas",       region: "Southwest",   desc: "the largest state market for CDL drivers" },
  florida:        { name: "Florida",         abbr: "FL", city: "Miami",        region: "Southeast",   desc: "a top-demand market for refrigerated and flatbed drivers" },
  georgia:        { name: "Georgia",         abbr: "GA", city: "Atlanta",      region: "Southeast",   desc: "a growing hub for logistics and CDL placements" },
  tennessee:      { name: "Tennessee",       abbr: "TN", city: "Nashville",    region: "Southeast",   desc: "a central corridor state for OTR and LTL drivers" },
  mississippi:    { name: "Mississippi",     abbr: "MS", city: "Jackson",      region: "South",       desc: "a growing market for semi-truck and box-truck drivers" },
  "north-carolina": { name: "North Carolina", abbr: "NC", city: "Charlotte",  region: "Southeast",   desc: "a high-demand market for regional CDL-A drivers" },
  indiana:        { name: "Indiana",         abbr: "IN", city: "Indianapolis", region: "Midwest",     desc: "an active market for OTR and team driving" },
  ohio:           { name: "Ohio",            abbr: "OH", city: "Columbus",     region: "Midwest",     desc: "a major hub for Midwest CDL driver placements" },
  california:     { name: "California",      abbr: "CA", city: "Los Angeles",  region: "West Coast",  desc: "a high-volume market for flatbed and reefer drivers" },
};

const DARK = "#0A0A0A";
const ACCENT = "#FF4D00";
const GOLD = "#F5C842";

const PROGRAM_HIGHLIGHTS = [
  { icon: DollarSign, label: "Top CPM Rates", desc: "Company drivers: $0.70–$1.00/mi · Lease: 80–88% gross" },
  { icon: Truck, label: "Semi & Box Truck", desc: "OTR flatbed, reefer, dry van · Local and regional routes" },
  { icon: Building2, label: "Carrier Competition", desc: "Multiple carriers bid — you choose the best offer" },
  { icon: Star, label: "Placement Fees Covered", desc: "All fees paid by carriers, never by drivers" },
];

export default function MarketState() {
  const { stateSlug } = useParams();
  const meta = STATE_META[stateSlug] || {
    name: stateSlug?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Your State",
    abbr: stateSlug?.toUpperCase().slice(0, 2) || "US",
    city: "your area",
    region: "the US",
    desc: "an active market for CDL drivers",
  };

  const title = `CDL Drivers in ${meta.name} | Hylas Companion Market`;
  const description = `Hylas Companion Market connects CDL-A drivers in ${meta.name} with top-paying carriers. Competitive placement bids, $0.70+ CPM, 80%+ lease purchase. Apply now from ${meta.city}.`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Hylas Companion CDL Driver Placement — ${meta.name}`,
    "description": description,
    "url": `https://hylascompanion.com/market/${stateSlug}`,
    "provider": {
      "@type": "Organization",
      "name": "Hylas Companion Trucking",
      "url": "https://hylascompanion.com",
      "telephone": "+1-708-395-9565"
    },
    "serviceType": "CDL Driver Recruitment",
    "areaServed": [
      { "@type": "State", "name": meta.name, "addressCountry": "US" }
    ],
    "offers": {
      "@type": "Offer",
      "description": `CDL-A driver placements in ${meta.name} starting from $1,500 placement fee. Carriers compete for pre-screened drivers.`
    }
  };

  return (
    <div className="min-h-screen" style={{ background: DARK, color: "#fff" }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`CDL driver jobs ${meta.name}, truck driver jobs ${meta.city}, CDL-A ${meta.abbr}, trucking jobs ${meta.name}, driver placement ${meta.name}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://hylascompanion.com/market/${stateSlug}`} />
        <meta name="geo.region" content={`US-${meta.abbr}`} />
        <meta name="geo.placename" content={`${meta.city}, ${meta.abbr}`} />
        <link rel="canonical" href={`https://hylascompanion.com/market/${stateSlug}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero */}
      <div
        className="relative overflow-hidden pt-32 pb-20 px-6"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% -5%, rgba(255,77,0,0.22) 0%, transparent 65%), ${DARK}`,
          borderBottom: `1px solid rgba(255,77,0,0.12)`,
        }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4" style={{ color: ACCENT }} />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">{meta.region} · {meta.abbr}</span>
          </div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-[0.04em] mb-4 leading-tight">
            CDL Drivers<br />
            <span style={{ color: ACCENT }}>in {meta.name}</span>
          </h1>
          <p className="text-white/55 text-lg max-w-2xl leading-relaxed mb-8">
            Hylas Companion Market is {meta.desc}. Pre-screened CDL-A drivers from {meta.city} and surrounding areas are placed with top carriers through our competitive bidding system.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 font-head text-sm font-black uppercase tracking-[0.2em] px-6 py-3.5 transition-all hover:brightness-110"
              style={{ background: ACCENT, color: "#fff" }}
            >
              Apply as a Driver <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/market"
              className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] px-6 py-3.5 border border-white/15 text-white/70 hover:border-white/40 hover:text-white transition-all"
            >
              View Transfer Board
            </Link>
          </div>
        </div>
      </div>

      {/* Pay highlights */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <p className="font-head text-[10px] uppercase tracking-[0.4em] text-white/30 mb-2">Why Drive Through Hylas</p>
        <h2 className="font-head font-black text-2xl uppercase tracking-[0.04em] mb-8" style={{ color: GOLD }}>
          {meta.name} Driver Benefits
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-14">
          {PROGRAM_HIGHLIGHTS.map(({ icon: Icon, label, desc }) => (
            <div
              key={label}
              className="p-5 flex items-start gap-4"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <div className="w-10 h-10 shrink-0 flex items-center justify-center" style={{ background: "rgba(255,77,0,0.1)", border: "1px solid rgba(255,77,0,0.2)" }}>
                <Icon className="w-5 h-5" style={{ color: ACCENT }} />
              </div>
              <div>
                <p className="font-head text-sm font-black uppercase tracking-[0.08em] text-white mb-1">{label}</p>
                <p className="text-xs text-white/45 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <p className="font-head text-[10px] uppercase tracking-[0.4em] text-white/30 mb-2">The Process</p>
        <h2 className="font-head font-black text-2xl uppercase tracking-[0.04em] mb-6 text-white">
          How Placement Works in {meta.name}
        </h2>
        <div className="space-y-4 mb-12">
          {[
            { num: "01", t: "Apply Online", d: `CDL-A drivers in ${meta.name} apply through the Hylas Companion website in under 2 minutes with no fees.` },
            { num: "02", t: "Safety Screening", d: "Our Safety team reviews your CDL, medical certificate, MVR, and PSP report before approving you for the Market." },
            { num: "03", t: "Carriers Compete", d: `Verified carriers bid for your placement with competitive CPM rates and placement fees. You see every offer.` },
            { num: "04", t: "You Choose", d: "Hylas Companion matches you with the best carrier offer and guides you through onboarding." },
          ].map(({ num, t, d }) => (
            <div key={num} className="flex gap-5 items-start p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <span className="font-head font-black text-2xl shrink-0" style={{ color: ACCENT, opacity: 0.5 }}>{num}</span>
              <div>
                <p className="font-head text-sm font-black uppercase tracking-[0.08em] text-white mb-0.5">{t}</p>
                <p className="text-xs text-white/45 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="p-8 text-center"
          style={{ background: "rgba(255,77,0,0.07)", border: "1px solid rgba(255,77,0,0.2)" }}
        >
          <p className="font-head text-xs uppercase tracking-[0.3em] text-white/30 mb-2">Ready to Start?</p>
          <h3 className="font-head font-black text-2xl uppercase tracking-[0.04em] mb-4" style={{ color: ACCENT }}>
            CDL Drivers in {meta.name} — Apply Today
          </h3>
          <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
            Join the Hylas Companion Market and let top carriers compete for your placement. No fees, no obligations.
          </p>
          <div className="flex justify-center flex-wrap gap-3">
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 font-head text-sm font-black uppercase tracking-[0.2em] px-8 py-3.5 transition-all hover:brightness-110"
              style={{ background: ACCENT, color: "#fff" }}
            >
              Apply Now — Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/market"
              className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] px-8 py-3.5 border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-all"
            >
              Enter as Carrier
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
